"use client";

import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cart, type CartItem } from "@/lib/cart-store";

interface Props {
  item: Omit<CartItem, "qty">;
  availability: "in_stock" | "on_order" | "expected" | "out_of_stock";
  externalId: string;
  productName: string;
  /** Компактный вариант для sticky bottom bar (mobile). */
  compact?: boolean;
}

const SALES_EMAIL = "sales@xn--j1ahie.xn--80adxhks";

function mailtoNotify(productName: string, externalId: string): string {
  const subject = encodeURIComponent(
    `Сообщить о поступлении: ${productName} (арт. ${externalId})`,
  );
  const body = encodeURIComponent(
    `Здравствуйте! Прошу сообщить, когда товар поступит на склад.\n\n` +
      `Артикул: ${externalId}\nНаименование: ${productName}\n`,
  );
  return `mailto:${SALES_EMAIL}?subject=${subject}&body=${body}`;
}

export function ProductPurchasePanel({
  item,
  availability,
  externalId,
  productName,
  compact = false,
}: Props) {
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);

  // Семантика: out_of_stock — единственный непокупаемый статус. on_order и
  // expected — товар можно заказать (менеджер уточнит срок).
  if (availability === "out_of_stock") {
    return (
      <div className={compact ? "ml-auto" : "mt-4"}>
        <a
          href={mailtoNotify(productName, externalId)}
          className={
            compact
              ? "inline-flex h-11 items-center justify-center rounded-[8px] border border-border-strong px-4 text-sm font-medium text-text hover:bg-bg-muted"
              : "inline-flex h-14 w-full items-center justify-center rounded-[8px] border border-border-strong px-7 text-base font-medium text-text hover:bg-bg-muted"
          }
        >
          Сообщить о&nbsp;поступлении
        </a>
      </div>
    );
  }

  const onAdd = () => {
    setBusy(true);
    cart.add({ ...item, qty });
    toast.success(`Добавлено в корзину (${qty} шт.)`, { description: item.name });
    setTimeout(() => setBusy(false), 500);
  };

  const dec = () => setQty((q) => Math.max(1, q - 1));
  const inc = () => setQty((q) => Math.min(999, q + 1));
  const stepperClass = compact
    ? "flex h-11 items-center rounded-[8px] border border-border"
    : "flex h-14 items-center rounded-[8px] border border-border";
  const stepperBtnClass = compact
    ? "h-full w-9 text-lg leading-none hover:bg-bg-muted disabled:opacity-40"
    : "h-full w-11 text-xl leading-none hover:bg-bg-muted disabled:opacity-40";
  const stepperInputClass = compact
    ? "h-full w-12 border-x border-border bg-transparent text-center text-sm tabular [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none focus:outline-none"
    : "h-full w-16 border-x border-border bg-transparent text-center text-base tabular [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none focus:outline-none";

  return (
    <div className={compact ? "ml-auto flex items-center gap-2" : "mt-4 flex flex-col gap-3"}>
      <div className={compact ? "flex items-center gap-2" : "flex items-stretch gap-3"}>
        <div className={stepperClass} role="group" aria-label="Количество">
          <button
            type="button"
            onClick={dec}
            disabled={qty <= 1}
            aria-label="Уменьшить количество"
            className={stepperBtnClass}
          >
            −
          </button>
          <input
            type="number"
            value={qty}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (!Number.isNaN(n)) setQty(Math.max(1, Math.min(999, n)));
            }}
            min={1}
            max={999}
            aria-label="Количество"
            className={stepperInputClass}
          />
          <button
            type="button"
            onClick={inc}
            disabled={qty >= 999}
            aria-label="Увеличить количество"
            className={stepperBtnClass}
          >
            +
          </button>
        </div>
        <Button
          size={compact ? "md" : "lg"}
          onClick={onAdd}
          disabled={busy}
          className={compact ? "" : "flex-1"}
        >
          В корзину
        </Button>
      </div>
    </div>
  );
}
